interface RawAction {
  actionId: number;
  name: string;
  colorCode: string;
  count: number;
  reasons: { reason: string; count: number }[];
}

interface RawDayData {
  date: string;
  actions: RawAction[];
}

export interface TransformedDataPoint {
  name: string; // дата
  [key: string]: number | string | { reason: string; count: number }[]; // динамічні поля для кожного actionId (також reasons)
}

export interface LineConfig {
  key: string; // dataKey для Recharts
  name: string; // відображуване ім'я в легенді
  color: string; // колір лінії
  reasons?: { reason: string; count: number }[];
}

export const prepareChartData = (
  rawData: RawDayData[],
): {
  data: TransformedDataPoint[];
  lines: LineConfig[];
} => {
  // 1. Збираємо унікальні actionId з усіх дат
  const actionMap = new Map<
    number,
    { name: string; colorCode: string; reasons: { reason: string; count: number }[] }
  >();
  for (const day of rawData) {
    for (const action of day.actions) {
      if (!actionMap.has(action.actionId)) {
        actionMap.set(action.actionId, {
          name: action.name,
          colorCode: action.colorCode,
          reasons: action.reasons,
        });
      }
    }
  }

  // 2. Створюємо конфігурацію ліній
  const lines: LineConfig[] = Array.from(actionMap.entries()).map(([id, info]) => ({
    key: `action_${id}`,
    name: info.name,
    color: info.colorCode,
    reasons: info.reasons,
  }));

  // 3. Перетворюємо дані у формат [{ name: 'дата', action_1: count, action_2: count, ... }]
  const data: TransformedDataPoint[] = rawData.map((day) => {
    const point: TransformedDataPoint = { name: day.date.split('-').reverse().join('.') };
    // initialize all known actions with 0 to avoid broken lines when an action is missing on a date
    const actionIds = Array.from(actionMap.keys());
    for (const id of actionIds) {
      point[`action_${id}`] = 0;
      point[`action_${id}_reasons`] = [];
    }
    for (const action of day.actions) {
      point[`action_${action.actionId}`] = action.count;
      // store per-date reasons for this action so tooltip can read correct reasons for the date
      point[`action_${action.actionId}_reasons`] = action.reasons;
    }
    // Для actionId, яких немає на цю дату, значення буде undefined, але Recharts це ігнорує.
    // Можна додати явно 0 для всіх actionId, але це не обов'язково.
    return point;
  });

  return { data, lines };
};
